import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card, Table, Button, Input, Select, Space, Modal,
  Form, notification, Spin,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ReloadOutlined,
  HomeOutlined, SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

export default function GramPramukhList() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal,   setModal]   = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [search,  setSearch]  = useState('');
  const [form] = Form.useForm();

  // Village live-search state
  const [villageOptions,      setVillageOptions]      = useState([]);
  const [villageSearching,    setVillageSearching]    = useState(false);
  const [selectedVillageId,   setSelectedVillageId]   = useState(null);
  const [selectedVillageNameEn, setSelectedVillageNameEn] = useState('');
  const villageTimerRef = useRef(null);

  // Member live-search state
  const [memberOptions,   setMemberOptions]   = useState([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const memberTimerRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/gram-pramukh`, { headers: auth() });
      if (r.data.status) setData(r.data.result || []);
    } catch { notification.error({ message: 'Failed to load', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Village search — debounced, /village/search?q= searches both English and Hindi
  const searchVillage = (q) => {
    clearTimeout(villageTimerRef.current);
    if (!q || q.length < 1) { setVillageOptions([]); setVillageSearching(false); return; }
    setVillageSearching(true);
    villageTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${baseUrl}/village/search`, { params: { q }, headers: auth() });
        setVillageOptions(
          (res.data.result || [])
            .filter(v => v.id) // only sn_village master entries with a real ID
            .map(v => {
              const parts = [v.name_en];
              if (v.name_hn) parts.push(v.name_hn);
              const loc = [v.tehsil_en, v.district_en].filter(Boolean).join(', ');
              return {
                value:   v.id,
                name_en: v.name_en, // stored so onChange can read it
                label:   loc ? `${parts.join(' / ')} — ${loc}` : parts.join(' / '),
              };
            })
        );
      } catch { setVillageOptions([]); }
      finally { setVillageSearching(false); }
    }, 300);
  };

  const buildMemberOptions = (result) =>
    (result || []).map(u => ({
      value: u.id,
      label: [
        u.name,
        u.father_name ? `(सु. ${u.father_name})` : null,
        u.phone_no    ? `— ${u.phone_no}` : null,
        u.village     ? `· ${u.village}` : null,
      ].filter(Boolean).join(' '),
    }));

  // Called immediately when a village is selected — no typing needed
  const loadVillageMembers = async (villageId, villageNameEn) => {
    if (!villageId && !villageNameEn) return;
    setMemberSearching(true);
    try {
      const params = {};
      if (villageId)     params.village_id      = villageId;
      if (villageNameEn) params.village_name_en  = villageNameEn;
      const res = await axios.get(`${baseUrl}/users/search-member`, { params, headers: auth() });
      setMemberOptions(buildMemberOptions(res.data.result));
    } catch { setMemberOptions([]); }
    finally { setMemberSearching(false); }
  };

  // Debounced typed search within the selected village
  const searchMember = (q) => {
    clearTimeout(memberTimerRef.current);
    const hasVillage = !!selectedVillageId;
    if (!hasVillage && (!q || q.length < 2)) {
      setMemberOptions([]);
      setMemberSearching(false);
      return;
    }
    setMemberSearching(true);
    memberTimerRef.current = setTimeout(async () => {
      try {
        const params = {};
        if (q && q.length >= 2)   params.q               = q;
        if (selectedVillageId)     params.village_id      = selectedVillageId;
        if (selectedVillageNameEn) params.village_name_en = selectedVillageNameEn;
        if (!params.q && !params.village_id) { setMemberOptions([]); return; }
        const res = await axios.get(`${baseUrl}/users/search-member`, { params, headers: auth() });
        setMemberOptions(buildMemberOptions(res.data.result));
      } catch { setMemberOptions([]); }
      finally { setMemberSearching(false); }
    }, 300);
  };

  const resetModal = () => {
    form.resetFields();
    setVillageOptions([]);
    setMemberOptions([]);
    setSelectedVillageId(null);
    setSelectedVillageNameEn('');
  };

  const handleAssign = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await axios.post(`${baseUrl}/admin/gram-pramukh`, values, { headers: auth() });
      notification.success({ message: 'Gram Pramukh Assigned', placement: 'topRight' });
      setModal(false);
      resetModal();
      fetchData();
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const handleRemove = (villageId) => {
    Modal.confirm({
      title: 'Remove Gram Pramukh assignment?', okType: 'danger', okText: 'Remove',
      onOk: async () => {
        await axios.delete(`${baseUrl}/admin/gram-pramukh/${villageId}`, { headers: auth() });
        fetchData();
      },
    });
  };

  const filtered = data.filter(d =>
    !search ||
    d.village_name_hn?.toLowerCase().includes(search.toLowerCase()) ||
    d.village_name_en?.toLowerCase().includes(search.toLowerCase()) ||
    d.member_name?.toLowerCase().includes(search.toLowerCase()) ||
    d.phone_no?.includes(search)
  );

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    {
      title: 'Village',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.village_name_hn || r.village_name_en || r.village_name || '—'}</div>
          {r.village_name_hn && r.village_name_en && (
            <div style={{ fontSize: 12, color: '#6b7280' }}>{r.village_name_en}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Gram Pramukh',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.member_name || '—'}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{r.phone_no || ''}</div>
        </div>
      ),
    },
    { title: 'Notes', dataIndex: 'notes', render: v => v || '—', ellipsis: true },
    { title: 'Since', dataIndex: 'assigned_at', width: 120, render: v => v?.slice(0, 10) || '—' },
    {
      title: 'Actions', width: 80,
      render: (_, r) => (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemove(r.village_id)} />
      ),
    },
  ];

  return (
    <div style={{ padding: '12px 0' }}>
      <Card
        title={<><HomeOutlined style={{ color: '#16a34a', marginRight: 8 }} />Gram Pramukh — Village Representatives</>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} />
            <Button
              type="primary" icon={<PlusOutlined />}
              onClick={() => { resetModal(); setModal(true); }}
            >
              Assign Pramukh
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 14 }}>
          <Input
            prefix={<SearchOutlined />} placeholder="Search village or name..."
            value={search} onChange={e => setSearch(e.target.value)}
            allowClear style={{ width: 300 }}
          />
        </div>
        <Table dataSource={filtered} columns={columns} rowKey="id" loading={loading} size="small" />
      </Card>

      <Modal
        open={modal} title="Assign Gram Pramukh"
        onCancel={() => { setModal(false); resetModal(); }}
        onOk={handleAssign} confirmLoading={saving} okText="Assign"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>

          {/* Village — live search (English + Hindi) via /village/search */}
          <Form.Item
            name="village_id" label="Village"
            rules={[{ required: true, message: 'Please select a village' }]}
          >
            <Select
              showSearch
              filterOption={false}
              placeholder="Type village name (English या हिन्दी)..."
              onSearch={searchVillage}
              notFoundContent={
                villageSearching
                  ? <Spin size="small" />
                  : <span style={{ fontSize: 12, color: '#9ca3af' }}>टाइप करें — English या हिन्दी में</span>
              }
              options={villageOptions}
              onChange={(val, option) => {
                const nameEn = option?.name_en || '';
                setSelectedVillageId(val);
                setSelectedVillageNameEn(nameEn);
                form.setFieldValue('user_id', undefined);
                setMemberOptions([]);
                // Immediately load all members from this village — no typing needed
                loadVillageMembers(val, nameEn);
              }}
            />
          </Form.Item>

          {/* Member — auto-list village members, then filter by typing */}
          <Form.Item
            name="user_id" label="Member (Gram Pramukh)"
            rules={[{ required: true, message: 'Please select a member' }]}
          >
            <Select
              showSearch
              filterOption={false}
              placeholder="नाम या phone नंबर से खोजें..."
              onSearch={searchMember}
              loading={memberSearching}
              notFoundContent={
                memberSearching
                  ? <Spin size="small" />
                  : <span style={{ fontSize: 12, color: '#9ca3af' }}>
                      {selectedVillageId
                        ? 'इस गाँव में कोई सदस्य नहीं मिला'
                        : 'पहले गाँव चुनें, या नाम / phone लिखें (2+ अक्षर)'}
                    </span>
              }
              options={memberOptions}
            />
          </Form.Item>

          <Form.Item name="notes" label="Notes (optional)">
            <Input placeholder="Any remarks..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
